import { Role } from '@zen/Auth/types';
import {
  analyticsRoutes,
  apiConsoleRoutes,
  bookingQuotesRoutes,
  bookingRequestRoutes,
  bookingShipmentsRoutes,
  bookingsRoutes,
  dashboardRoutes,
  financeRoutes,
  intakePlanningRoutes,
  productRoutes,
  purchaseOrderRoutes,
  quoteRoutes,
  rateCardRoutes,
  shipmentRoutes,
  utilisationRoutes,
  warehouseIntakeRoutes
} from '@zen/Routes';
import financialsRoutes from '@zen/Routes/financials';

import type { SidebarLink } from '../types';
import type { CustomerLinksFeatureFlags } from './types';

export const getConfig = (
  canAccessApiConsole: boolean,
  canAccessFreightSpend: boolean,
  featureFlags: CustomerLinksFeatureFlags,
  role: Role
): SidebarLink[] => {
  const isAgent: boolean = role === Role.AGENT_FORWARDER;
  const isManufacturer: boolean = role === Role.MANUFACTURER;
  const {
    isAskZenseiEnabled,
    isBookingsPageEnabled,
    isFinancialsEnabled,
    isFreightSpendEnabled,
    isQuotesPageEnabled,
    isRateCardsEnabled,
    isShipmentsPageEnabled,
    isUtilisationEnabled
  } = featureFlags;
  const canAccessWarehousing: boolean = !isAgent && !isManufacturer;

  return [
    {
      icon: 'zicon-dashboard',
      label: 'Dashboard',
      url: dashboardRoutes.dashboard.getUrl(),
      isExact: true
    },
    {
      icon: 'zicon-orders',
      label: 'Orders',
      url: purchaseOrderRoutes.index.getUrl()
    },
    {
      icon: 'zicon-quotes',
      label: 'Quotes',
      url: quoteRoutes.quoteIndex.getUrl(),
      counter: 'quotesCount'
    },
    {
      isExpandedByDefault: true,
      isSection: true,
      label: 'Book',
      url: '',
      children: [
        ...(isQuotesPageEnabled
          ? [
              {
                icon: 'zicon-quotes' as const,
                label: 'Quotes',
                url: bookingQuotesRoutes.index.getUrl()
              }
            ]
          : []),
        ...(isBookingsPageEnabled
          ? [
              {
                icon: 'zicon-booking-request' as const,
                label: 'Bookings',
                url: bookingsRoutes.index.getUrl()
              }
            ]
          : [])
      ]
    },
    {
      isExpandedByDefault: true,
      isSection: true,
      label: 'Track',
      url: '',
      children: [
        ...(isShipmentsPageEnabled
          ? [
              {
                icon: 'zicon-shipment' as const,
                label: 'Shipments',
                url: bookingShipmentsRoutes.index.getUrl()
              }
            ]
          : [])
      ]
    },
    ...(isRateCardsEnabled
      ? [
          {
            icon: 'zicon-finance' as const,
            label: 'Rate cards',
            url: rateCardRoutes.index.getUrl()
          }
        ]
      : []),
    {
      icon: 'zicon-booking-request',
      label: 'Booking requests',
      url: bookingRequestRoutes.requestIndex.getUrl(),
      counter: 'bookingRequestsCount'
    },
    {
      icon: 'zicon-shipment',
      label: 'Shipments',
      url: shipmentRoutes.shipmentIndex.getUrl()
    },
    {
      icon: 'zicon-tag',
      label: 'Products',
      url: productRoutes.index.getUrl()
    },
    ...(canAccessApiConsole
      ? [
          {
            icon: 'zicon-api' as const,
            label: 'API console',
            url: apiConsoleRoutes.index.getUrl()
          }
        ]
      : []),
    {
      icon: 'zicon-intake-planning',
      label: 'Insights',
      url: '',
      children: [
        ...(isAskZenseiEnabled
          ? [
              {
                isHighlighted: true,
                label: 'Ask Luca',
                url: analyticsRoutes.conversationalAnalytics.getUrl()
              }
            ]
          : []),
        {
          label: 'Analytics',
          url: analyticsRoutes.index.getUrl()
        },
        ...(isFinancialsEnabled
          ? [
              {
                label: 'Financials',
                url: financialsRoutes.index.getUrl()
              }
            ]
          : []),
        {
          label: 'Intake planning',
          url: intakePlanningRoutes.index.getUrl()
        },
        ...(isUtilisationEnabled
          ? [
              {
                label: 'Utilisation',
                url: utilisationRoutes.index.getUrl()
              }
            ]
          : []),
        ...(isFreightSpendEnabled && canAccessFreightSpend
          ? [
              {
                label: 'Freight spend',
                url: financeRoutes.index.getUrl()
              }
            ]
          : []),
        ...(canAccessWarehousing
          ? [
              {
                label: 'Warehousing',
                url: warehouseIntakeRoutes.index.getUrl()
              }
            ]
          : [])
      ]
    }
  ];
};
